/* ============================================================================
   rcds-home.js — RolaCase US homepage motion (ports v13/V1.2 app.js behaviour)
   ----------------------------------------------------------------------------
   Two features, both progressive-enhancement and SAFE-BY-DEFAULT:

   1) SCROLL REVEAL — elements with .rcds-reveal fade + slide up as they enter the
      viewport (V1.2 initReveal, IntersectionObserver threshold .12, rootMargin
      -8%). The reveal is gated on the .rcds-home-js class this script adds to
      <html> FIRST THING, so the opacity:0 start state ONLY applies when JS runs.
      If this file 404s / is CSP-blocked / JS is off, the .rcds-home-js class is
      never added, .rcds-reveal stays fully visible, and nothing is ever hidden.
      prefers-reduced-motion is also honoured (CSS + here: reveal-all immediately).

   2) RAIL NAV — prev/next buttons scroll a horizontal rail by one card step
      (V1.2 railNav). Wires every [data-rcds-rail] to its sibling
      [data-rcds-rail-prev] / [data-rcds-rail-next] buttons.

   No dependencies, no globals leaked (IIFE). Self-loaded once from
   sections/rcds-home-hero.liquid (the first homepage section).
   ========================================================================== */
(function () {
  'use strict';

  // Mark the document as JS-enabled BEFORE first paint work, so the reveal
  // opacity:0 start-state (scoped to .rcds-home-js in CSS) is only ever active
  // when this script is actually running.
  var root = document.documentElement;
  root.classList.add('rcds-home-js');

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---- 1) scroll reveal --------------------------------------------------- */
  function initReveal() {
    var els = all('.rcds-reveal');
    if (!els.length) return;
    // No IO or reduced motion -> reveal everything immediately (never leave hidden).
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-rcds-in'); });
      return;
    }
    function show(el) { el.classList.add('is-rcds-in'); }
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (en) {
        // Reveal when it enters, AND when it has been scrolled ABOVE the viewport
        // (top < 0) — so an element the user jumped past (anchor link / fast flick)
        // still ends up visible instead of stuck at opacity:0.
        if (en.isIntersecting || en.boundingClientRect.top < 0) { show(en.target); io.unobserve(en.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (e) { io.observe(e); });

    // FAILSAFE: IntersectionObserver can miss an element that is jumped over between
    // two instant scroll positions (it never registers an intersecting frame). A
    // throttled scroll/resize sweep guarantees anything that has entered the viewport
    // is revealed, so no section header is ever left invisible. Runs once on load too.
    var ticking = false;
    function sweep() {
      ticking = false;
      var vh = window.innerHeight;
      els.forEach(function (el) {
        if (el.classList.contains('is-rcds-in')) return;
        if (el.getBoundingClientRect().top < vh * 0.92) { show(el); io.unobserve(el); }
      });
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(sweep); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', sweep);
    sweep();
  }

  /* ---- 2) rail prev/next -------------------------------------------------- */
  function initRailNav() {
    all('[data-rcds-rail]').forEach(function (rail) {
      var key = rail.getAttribute('data-rcds-rail');
      // one "step" = the width of the first card + the grid gap, so a click
      // advances exactly one card (falls back to 80% of the rail width).
      function step() {
        var first = rail.firstElementChild;
        if (!first) return Math.round(rail.clientWidth * 0.8);
        var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || '0') || 0;
        return Math.round(first.getBoundingClientRect().width + gap);
      }
      var prev = document.querySelector('[data-rcds-rail-prev="' + key + '"]');
      var next = document.querySelector('[data-rcds-rail-next="' + key + '"]');
      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });

      // Dim the prev/next buttons at the ends (nice-to-have; never blocks scroll).
      function sync() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.toggleAttribute('data-rcds-rail-end', rail.scrollLeft <= 2);
        if (next) next.toggleAttribute('data-rcds-rail-end', rail.scrollLeft >= max);
      }
      rail.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  }

  // Reveal now runs universally from rcds-motion.js (loaded on every page via the
  // nav). This file keeps only the homepage rail-nav. initReveal retained but
  // unused to avoid double-observing .rcds-reveal.
  function boot() { initRailNav(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
